import { Link } from 'react-router-dom'
import { Package, Shield, CheckCircle, FileKey, Activity, Code, Lock } from 'lucide-react'
import { PackageSummary } from '../utils/api'

interface PackageCardProps {
  package: PackageSummary
}

export default function PackageCard({ package: pkg }: PackageCardProps) {
  const getBadgeConfig = (badge: string) => {
    switch (badge) {
      case 'approved':
        return { icon: Shield, className: 'badge-approved', label: 'Approved' }
      case 'verified_publisher':
        return { icon: CheckCircle, className: 'badge-verified', label: 'Verified' }
      case 'signed_release':
        return { icon: FileKey, className: 'badge-signed', label: 'Signed' }
      case 'healthy_endpoint':
        return { icon: Activity, className: 'bg-emerald-100 text-emerald-800', label: 'Healthy' }
      default:
        return null
    }
  }

  return (
    <Link
      to={`/skills/${pkg.slug}`}
      className="card p-6 hover:shadow-md transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-start space-x-4">
        {pkg.icon_url ? (
          <img
            src={pkg.icon_url}
            alt={pkg.name}
            className="w-12 h-12 rounded-lg object-cover"
          />
        ) : (
          <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-accent-100 rounded-lg flex items-center justify-center">
            <Package className="w-6 h-6 text-primary-600" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">{pkg.name}</h3>
          <p className="text-sm text-gray-500 truncate">{pkg.slug}</p>
        </div>
      </div>

      {pkg.summary && (
        <p className="mt-3 text-sm text-gray-600 line-clamp-2">{pkg.summary}</p>
      )}

      {/* Badges */}
      <div className="mt-4 flex flex-wrap gap-2">
        {pkg.open_source ? (
          <span className="badge bg-emerald-100 text-emerald-800">
            <Code className="w-3 h-3 mr-1" />
            Open Source
          </span>
        ) : (
          <span className="badge bg-gray-100 text-gray-600">
            <Lock className="w-3 h-3 mr-1" />
            Proprietary
          </span>
        )}
        {pkg.trust_tier === 'approved' && (
          <span className="badge badge-approved">
            <Shield className="w-3 h-3 mr-1" />
            Approved
          </span>
        )}
        {pkg.badges.filter(b => b !== 'approved').slice(0, 1).map((badge) => {
          const config = getBadgeConfig(badge)
          if (!config) return null
          return (
            <span key={badge} className={`badge ${config.className}`}>
              <config.icon className="w-3 h-3 mr-1" />
              {config.label}
            </span>
          )
        })}
      </div>

      {/* Tags */}
      {pkg.tags && pkg.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {pkg.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs text-gray-600 bg-gray-100"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Publisher */}
      {pkg.publisher_name && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            by <span className="text-gray-700 font-medium">{pkg.publisher_name}</span>
          </p>
        </div>
      )}
    </Link>
  )
}
